import { useEffect, useRef, useState } from "react";

const healthyFeatures = [
  "Healthy Lubrication",
  "Smooth Movement",
  "Flexibility",
  "Everyday Comfort",
];

const agingFeatures = [
  "Reduced Lubrication",
  "Increased Stiffness",
  "Less Comfortable Movement",
  "Reduced Flexibility",
];

export default function ScienceSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="science" ref={ref} className="py-16 md:py-24 bg-background-100">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-8 lg:px-12">
        <div
          className={`text-center mb-12 md:mb-16 transition-all duration-600 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
          }`}
          style={{ transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)" }}
        >
          <p className="text-accent-500 text-base font-medium tracking-[0.2em] uppercase mb-3">
            The Science
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            Healthy Joint vs Aging Joint
          </h2>
          <p className="text-foreground-700 text-xl max-w-2xl mx-auto">
            What changes inside the joint capsule over time -- and what supports
            comfort.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <div
            className={`rounded-xl p-6 md:p-8 border border-background-200/70 bg-background-50 card-hover-lift transition-all duration-600 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
            }`}
            style={{
              transitionDelay: "0ms",
              transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)",
            }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-4 h-4 rounded-full bg-emerald-500" />
              <h3 className="font-heading text-2xl font-semibold text-foreground-950">
                Healthy Joint
              </h3>
            </div>
            <ul className="space-y-3">
              {healthyFeatures.map((feat) => (
                <li key={feat} className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-700 shrink-0">
                    <i className="ri-check-line text-base" />
                  </div>
                  <span className="text-foreground-800 text-lg font-medium">{feat}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            className={`rounded-xl p-6 md:p-8 border border-background-200/70 bg-background-50 card-hover-lift transition-all duration-600 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
            }`}
            style={{
              transitionDelay: "80ms",
              transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)",
            }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-4 h-4 rounded-full bg-accent-500" />
              <h3 className="font-heading text-2xl font-semibold text-foreground-950">
                Aging Joint
              </h3>
            </div>
            <ul className="space-y-3">
              {agingFeatures.map((feat) => (
                <li key={feat} className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
                    <i className="ri-close-line text-base" />
                  </div>
                  <span className="text-foreground-800 text-lg font-medium">{feat}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}